/**
 * LRC 文件解析工具
 * 解析 LRC 格式的歌词文件，返回带时间戳的歌词数组
 */

/**
 * 将 LRC 时间戳字符串转换为秒数
 * @param {string} timeString - LRC 时间戳格式，如 "01:23.456" 或 "1:23.456"
 * @returns {number} 秒数
 */
export const parseLRCTime = (timeString) => {
    // 匹配格式：[mm:ss.xxx] 或 [m:ss.xxx]
    const match = timeString.match(/\[(\d+):(\d+)\.(\d+)\]/);
    if (!match) return null;
    
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    const milliseconds = parseInt(match[3], 10);
    
    return minutes * 60 + seconds + milliseconds / 1000;
};

/**
 * 解析 LRC 文件内容
 * @param {string} lrcContent - LRC 文件内容
 * @returns {Array<{time: number, text: string}>} 带时间戳的歌词数组，按时间排序
 */
export const parseLRC = (lrcContent) => {
    if (!lrcContent) return [];
    
    const lines = lrcContent.split('\n');
    const lyrics = [];
    let title = 'Lyrics';
    
    for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;
        
        // 先检查是否是时间戳格式（避免误匹配元数据）
        const hasTimeStamp = /\[\d+:\d+\.\d+\]/.test(trimmedLine);
        
        // 解析元数据标签，如 [ti:标题]
        // 只匹配冒号前面是字母的情况，避免匹配时间戳
        if (!hasTimeStamp) {
            const metaMatch = trimmedLine.match(/^\[([a-zA-Z]+):(.+)\]$/);
            if (metaMatch) {
                const [, key, value] = metaMatch;
                if (key === 'ti') {
                    title = value;
                }
                continue;
            }
        }
        
        // 解析歌词行，可能包含多个时间戳
        // 格式：[mm:ss.xxx]歌词文本 或 [mm:ss.xxx][mm:ss.xxx]歌词文本
        const timeMatches = [...trimmedLine.matchAll(/\[(\d+):(\d+)\.(\d+)\]/g)];
        if (timeMatches.length === 0) continue;
        
        // 提取歌词文本（移除所有时间戳）
        const text = trimmedLine.replace(/\[\d+:\d+\.\d+\]/g, '').trim();
        if (!text) continue;
        
        // 为每个时间戳创建一条歌词记录
        for (const match of timeMatches) {
            const timeString = match[0];
            const time = parseLRCTime(timeString);
            if (time !== null) {
                lyrics.push({ time, text });
            }
        }
    }
    
    // 按时间排序
    lyrics.sort((a, b) => a.time - b.time);
    
    return { lyrics, title };
};

/**
 * 根据当前时间找到应该高亮的歌词索引
 * @param {Array<{time: number, text: string}>} lyrics - 带时间戳的歌词数组
 * @param {number} currentTime - 当前播放时间（秒）
 * @returns {number} 应该高亮的歌词索引，-1 表示没有匹配的歌词
 */
export const findCurrentLyricIndex = (lyrics, currentTime) => {
    if (!lyrics || lyrics.length === 0) return -1;
    
    // 找到最后一个时间小于等于当前时间的歌词
    let index = -1;
    for (let i = 0; i < lyrics.length; i++) {
        if (lyrics[i].time <= currentTime) {
            index = i;
        } else {
            break;
        }
    }
    
    return index;
};

